import { Fragment, type ReactNode } from 'react';
import { Link } from 'wouter';

/**
 * The little Markdown a chat answer uses — paragraphs, lists, tables,
 * headings, code, bold, italics and links — built as React elements, so
 * nothing the model writes is ever parsed as HTML. A link into the app
 * (`/?fund=…`) navigates in place, which is what opens the fund pane; any
 * other link opens in a new tab.
 */
export function Markdown(props: { text: string }): React.JSX.Element {
  return <div className="assistant-markdown">{blocks(props.text)}</div>;
}

function blocks(text: string): ReactNode[] {
  const lines = text.replace(/\r\n/g, '\n').split('\n');
  const out: ReactNode[] = [];
  let index = 0;
  while (index < lines.length) {
    const line = lines[index] ?? '';
    const key = `b${index}`;

    if (line.trim() === '') {
      index += 1;
      continue;
    }

    if (line.startsWith('```')) {
      const body: string[] = [];
      index += 1;
      while (index < lines.length && !(lines[index] ?? '').startsWith('```')) {
        body.push(lines[index] ?? '');
        index += 1;
      }
      index += 1;
      out.push(
        <pre key={key}>
          <code>{body.join('\n')}</code>
        </pre>,
      );
      continue;
    }

    const heading = /^(#{1,6})\s+(.*)$/.exec(line);
    if (heading !== null) {
      out.push(
        <p key={key} className="assistant-heading">
          {inline(heading[2] ?? '')}
        </p>,
      );
      index += 1;
      continue;
    }

    if (isTableRow(line) && isTableRule(lines[index + 1] ?? '')) {
      const header = cells(line);
      const rows: string[][] = [];
      index += 2;
      while (index < lines.length && isTableRow(lines[index] ?? '')) {
        rows.push(cells(lines[index] ?? ''));
        index += 1;
      }
      out.push(
        <div key={key} className="assistant-table">
          <table>
            <thead>
              <tr>
                {header.map((cell, column) => (
                  <th key={column}>{inline(cell)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {row.map((cell, column) => (
                    <td key={column}>{inline(cell)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
      continue;
    }

    const bullet = /^\s*([-*+]|\d+[.)])\s+/;
    if (bullet.test(line)) {
      const ordered = /^\s*\d/.test(line);
      const items: string[] = [];
      while (index < lines.length && bullet.test(lines[index] ?? '')) {
        let item = (lines[index] ?? '').replace(bullet, '');
        index += 1;
        // A wrapped line indented under its item belongs to it.
        while (
          index < lines.length &&
          /^\s{2,}\S/.test(lines[index] ?? '') &&
          !bullet.test(lines[index] ?? '')
        ) {
          item += ` ${(lines[index] ?? '').trim()}`;
          index += 1;
        }
        items.push(item);
      }
      const children = items.map((item, itemIndex) => <li key={itemIndex}>{inline(item)}</li>);
      out.push(ordered ? <ol key={key}>{children}</ol> : <ul key={key}>{children}</ul>);
      continue;
    }

    const paragraph: string[] = [];
    while (
      index < lines.length &&
      (lines[index] ?? '').trim() !== '' &&
      !bullet.test(lines[index] ?? '') &&
      !(lines[index] ?? '').startsWith('```') &&
      !/^#{1,6}\s/.test(lines[index] ?? '') &&
      !isTableRow(lines[index] ?? '')
    ) {
      paragraph.push(lines[index] ?? '');
      index += 1;
    }
    if (paragraph.length === 0) {
      // A table row with no rule under it reads as a plain line.
      paragraph.push(line);
      index += 1;
    }
    out.push(
      <p key={key}>
        {paragraph.map((part, partIndex) => (
          <Fragment key={partIndex}>
            {partIndex > 0 && <br />}
            {inline(part)}
          </Fragment>
        ))}
      </p>,
    );
  }
  return out;
}

function isTableRow(line: string): boolean {
  return /^\s*\|.*\|\s*$/.test(line);
}

function isTableRule(line: string): boolean {
  return /^\s*\|(\s*:?-{2,}:?\s*\|)+\s*$/.test(line);
}

function cells(line: string): string[] {
  return line
    .trim()
    .replace(/^\||\|$/g, '')
    .split('|')
    .map((cell) => cell.trim());
}

/** `code`, [text](href), **bold**, *italic* / _italic_, in that order of precedence. */
const INLINE =
  /(`[^`]+`)|\[([^\]]+)\]\(([^)\s]+)\)|\*\*([^*]+)\*\*|(?<![\w*])\*([^*\s][^*]*)\*(?!\w)|(?<!\w)_([^_\s][^_]*)_(?!\w)/g;

/** A model puts `<br>` in a table cell to stack names; it is the one tag read, as a line break. */
function inline(text: string): ReactNode[] {
  const lines = text.split(/<br\s*\/?>/i);
  if (lines.length > 1) {
    return lines.flatMap((line, index) => [
      ...(index > 0 ? [<br key={`br${index}`} />] : []),
      <Fragment key={`l${index}`}>{inline(line)}</Fragment>,
    ]);
  }
  const out: ReactNode[] = [];
  let last = 0;
  for (const match of text.matchAll(INLINE)) {
    const at = match.index ?? 0;
    if (at > last) out.push(text.slice(last, at));
    const key = `i${at}`;
    const [whole, code, linkText, href, bold, italic, underscored] = match;
    if (code !== undefined) out.push(<code key={key}>{code.slice(1, -1)}</code>);
    else if (linkText !== undefined && href !== undefined) {
      out.push(<MarkdownLink key={key} href={href} text={linkText} />);
    } else if (bold !== undefined) out.push(<strong key={key}>{inline(bold)}</strong>);
    else if (italic !== undefined) out.push(<em key={key}>{inline(italic)}</em>);
    else if (underscored !== undefined) out.push(<em key={key}>{inline(underscored)}</em>);
    else out.push(whole);
    last = at + whole.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

function MarkdownLink(props: { href: string; text: string }): React.JSX.Element {
  if (props.href.startsWith('/') && !props.href.startsWith('//')) {
    return <Link href={props.href}>{inline(props.text)}</Link>;
  }
  if (/^https?:\/\//i.test(props.href)) {
    return (
      <a href={props.href} target="_blank" rel="noreferrer noopener">
        {inline(props.text)}
      </a>
    );
  }
  // Anything else (javascript:, data:, a bare word) is shown, never followed.
  return <span>{inline(props.text)}</span>;
}
